import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { useApi } from '@/hooks/useApi';
import { articleAdminApi } from '@/features/admin/articleAdminApi';
import type {
  ArticleStatsEntry,
  ArticleStatsResponse,
  CountryArticleStatsEntry,
} from '@/features/admin/articleAdminApi';

interface TopViewRequest {
  label: string;
  groupType: 'category' | 'topic';
  code: string;
  country?: string;
}

function StatsGrid({
  title,
  subtitle,
  entries,
  onClickViews,
}: {
  title: string;
  subtitle: string;
  entries: ArticleStatsEntry[];
  onClickViews: (request: TopViewRequest) => void;
}): React.JSX.Element {
  return (
    <Card className="p-6">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
        </div>
        <span className="text-sm text-gray-500">{entries.length} item(s)</span>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-gray-500">No data available.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {entries.map(entry => {
            const unpublishedCount = Math.max(entry.createdCount - entry.publishedCount, 0);

            return (
              <div
                key={entry.code}
                className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-sm"
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                      {entry.code}
                    </p>
                    <h3 className="mt-1 text-lg font-semibold text-gray-800">{entry.nameEN}</h3>
                    <p className="text-sm text-gray-500">{entry.nameVI}</p>
                  </div>
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                    Live
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-gray-100 p-3">
                    <p className="text-gray-500">Created</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">
                      {entry.createdCount.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-xl bg-green-50 p-3">
                    <p className="text-green-700">Published</p>
                    <p className="mt-1 text-2xl font-bold text-green-700">
                      {entry.publishedCount.toLocaleString()}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="mt-3 text-left text-sm font-semibold text-blue-700 underline"
                  onClick={() =>
                    onClickViews({
                      label: `${entry.nameEN} (${entry.code})`,
                      groupType: 'topic',
                      code: entry.code,
                    })
                  }
                >
                  Total views: {entry.totalViews.toLocaleString()}
                </button>

                <p className="mt-3 text-xs text-gray-500">
                  {unpublishedCount.toLocaleString()} draft or scheduled item(s)
                </p>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function CountryStatsGrid({
  entries,
  onClickViews,
}: {
  entries: CountryArticleStatsEntry[];
  onClickViews: (request: TopViewRequest) => void;
}): React.JSX.Element {
  return (
    <Card className="p-6">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Country Articles</h2>
          <p className="mt-1 text-sm text-gray-500">Category totals inside each country table</p>
        </div>
        <span className="text-sm text-gray-500">{entries.length} country(s)</span>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-gray-500">No data available.</p>
      ) : (
        <div className="space-y-5">
          {entries.map(country => (
            <div key={country.code} className="rounded-2xl border border-gray-200 bg-white p-4">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    {country.code}
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-gray-800">{country.nameEN}</h3>
                  <p className="text-sm text-gray-500">{country.nameVI}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-gray-100 px-3 py-2">
                    <p className="text-gray-500">Created</p>
                    <p className="text-xl font-bold text-gray-900">
                      {country.createdCount.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-xl bg-green-50 px-3 py-2">
                    <p className="text-green-700">Published</p>
                    <p className="text-xl font-bold text-green-700">
                      {country.publishedCount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <p className="mb-4 text-sm font-semibold text-blue-700">
                Total views: {country.totalViews.toLocaleString()}
              </p>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {country.categories.length === 0 ? (
                  <p className="text-sm text-gray-500">No categories found for this country.</p>
                ) : (
                  country.categories.map(category => (
                    <div
                      key={category.code}
                      className="rounded-xl border border-gray-200 bg-gray-50 p-4"
                    >
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {category.code}
                          </p>
                          <h4 className="mt-1 text-base font-semibold text-gray-800">
                            {category.nameEN}
                          </h4>
                          <p className="text-sm text-gray-500">{category.nameVI}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="rounded-lg bg-white p-3">
                          <p className="text-gray-500">Created</p>
                          <p className="mt-1 text-lg font-bold text-gray-900">
                            {category.createdCount.toLocaleString()}
                          </p>
                        </div>
                        <div className="rounded-lg bg-white p-3">
                          <p className="text-gray-500">Published</p>
                          <p className="mt-1 text-lg font-bold text-green-700">
                            {category.publishedCount.toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="mt-3 text-left text-sm font-semibold text-blue-700 underline"
                        onClick={() =>
                          onClickViews({
                            label: `${country.nameEN} / ${category.nameEN}`,
                            groupType: 'category',
                            code: category.code,
                            country: country.code,
                          })
                        }
                      >
                        Total views: {category.totalViews.toLocaleString()}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export const ArticleStatistics: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    data: stats,
    loading,
    error,
    execute,
  } = useApi<ArticleStatsResponse>(articleAdminApi.getStats);

  useEffect(() => {
    void execute();
  }, [execute]);

  const createdCount = stats?.summary.totalCreated ?? 0;
  const publishedCount = stats?.summary.totalPublished ?? 0;
  const totalViews = stats?.summary.totalViews ?? 0;
  const publishedRate = createdCount > 0 ? Math.round((publishedCount / createdCount) * 100) : 0;

  const handleShowTopViews = (request: TopViewRequest): void => {
    const searchParams = new URLSearchParams();
    searchParams.set('groupType', request.groupType);
    searchParams.set('code', request.code);
    searchParams.set('label', request.label);
    if (request.country) {
      searchParams.set('country', request.country);
    }

    navigate(`/admin/article-top-views?${searchParams.toString()}`);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 rounded-3xl border border-gray-200 bg-gradient-to-r from-slate-50 via-white to-amber-50 p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Article statistics</h1>
            <p className="mt-2 text-gray-600">Welcome back, {user?.name}!</p>
          </div>
          <Link to="/admin/dashboard">
            <Button variant="outline">Back to Dashboard</Button>
          </Link>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card className="p-5">
            <p className="text-sm font-medium text-gray-500">Total articles created</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {loading ? '...' : createdCount.toLocaleString()}
            </p>
          </Card>
          <Card className="p-5">
            <p className="text-sm font-medium text-gray-500">Total articles published</p>
            <p className="mt-2 text-3xl font-bold text-green-700">
              {loading ? '...' : publishedCount.toLocaleString()}
            </p>
          </Card>
          <Card className="p-5">
            <p className="text-sm font-medium text-gray-500">Publication rate</p>
            <p className="mt-2 text-3xl font-bold text-amber-700">
              {loading ? '...' : `${publishedRate}%`}
            </p>
          </Card>
          <Card className="p-5 md:col-span-3">
            <p className="text-sm font-medium text-gray-500">Total article views</p>
            <p className="mt-2 text-3xl font-bold text-blue-700">
              {loading ? '...' : totalViews.toLocaleString()}
            </p>
          </Card>
        </div>
      </div>

      {error ? (
        <Card className="mb-8 border-red-200 bg-red-50 p-6">
          <p className="text-red-800">Error loading article stats: {error.message}</p>
          <Button className="mt-4" onClick={() => execute()}>
            Retry
          </Button>
        </Card>
      ) : null}

      <div className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <CountryStatsGrid entries={stats?.countries || []} onClickViews={handleShowTopViews} />
        <StatsGrid
          title="Topic Articles"
          subtitle="Created, published, and total views by topic"
          entries={stats?.topics || []}
          onClickViews={handleShowTopViews}
        />
      </div>
    </div>
  );
};
